import { createPaintLayer, getLayer, patchMask, updateLayer } from '../core/model/document';
import { insertNextTo } from '../core/model/tree';
import type { EditorDocument, ImageLayer, Layer, PaintLayer } from '../core/model/types';
import { blankLayerDataUrl } from '../io/raster';
import type { AppContext } from './context';

/**
 * Where a brush stroke lands.
 *
 * Painting can target a paint layer or an image layer; both are raster, but they
 * differ in field name, intrinsic size and scale. A **mask** is a third target:
 * the same geometry as its layer, because a mask lives in the layer's pixel space,
 * with a different field to write back to. Resolving that here keeps the
 * interaction hook free of model details, and gives one place to explain a refusal.
 */

export interface PaintTarget {
  layerId: string;
  /** Where the baked pixels are written back to. */
  field: 'pixels' | 'src' | 'mask';
  src: string;
  /** Intrinsic size of the layer's own pixel buffer. */
  width: number;
  height: number;
  /** Where the layer sits in document space, and how big it looks there. */
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
}

export type TargetRejection = 'no-layer' | 'locked' | 'hidden' | 'unsupported';

export function resolvePaintTarget(
  doc: EditorDocument,
  activeLayerId: string | null,
  maskEditing: string | null = null,
): { target: PaintTarget | null; reason?: TargetRejection } {
  const layer = activeLayerId ? getLayer(doc, activeLayerId) : undefined;
  if (!layer) return { target: null, reason: 'no-layer' };
  if (layer.locked) return { target: null, reason: 'locked' };
  if (!layer.visible) return { target: null, reason: 'hidden' };
  // Painting the mask instead of the layer: same geometry, different field, and
  // only while that layer's mask is the one being edited.
  if (maskEditing && maskEditing === layer.id && layer.mask?.src) {
    const base = targetOf(layer);
    if (base) return { target: { ...base, field: 'mask', src: layer.mask.src }, reason: undefined };
  }
  return { target: targetOf(layer) ?? null, reason: undefined };
}

function targetOf(layer: Layer): PaintTarget | null {
  if (layer.type === 'paint') {
    const paint = layer as PaintLayer;
    return {
      layerId: paint.id,
      field: 'pixels',
      src: paint.pixels,
      // The layer's own raster size — canvas-sized at creation, but smaller
      // once a crop or rotate has baked its pixels down.
      width: paint.naturalWidth,
      height: paint.naturalHeight,
      x: layer.x,
      y: layer.y,
      scaleX: layer.scaleX,
      scaleY: layer.scaleY,
    };
  }
  if (layer.type === 'image') {
    const image = layer as ImageLayer;
    return {
      layerId: image.id,
      field: 'src',
      src: image.src,
      width: image.naturalWidth,
      height: image.naturalHeight,
      x: layer.x,
      y: layer.y,
      scaleX: layer.scaleX,
      scaleY: layer.scaleY,
    };
  }
  return null;
}

/**
 * The layer to paint on, creating one when the active layer cannot take paint.
 *
 * Refusing to draw on a fresh document because "there is no paint layer" is the
 * kind of friction that makes an editor feel hostile, so the first stroke makes
 * the layer it needs and says so.
 */
export function ensurePaintTarget(ctx: AppContext): PaintTarget | null {
  const { doc, activeLayerId, maskEditing, commit, setActiveLayer, notify } = ctx.store.getState();
  const resolved = resolvePaintTarget(doc, activeLayerId, maskEditing);
  if (resolved.target) return resolved.target;

  if (resolved.reason === 'locked' || resolved.reason === 'hidden') {
    notify(
      resolved.reason === 'locked'
        ? 'That layer is locked.'
        : 'That layer is hidden — show it to paint on it.',
      'warn',
    );
    return null;
  }

  const layer = createPaintLayer({
    name: `Layer ${doc.layers.length + 1}`,
    pixels: blankLayerDataUrl(doc.width, doc.height),
    naturalWidth: doc.width,
    naturalHeight: doc.height,
    x: 0,
    y: 0,
  });
  // The new layer lands next to the active one, inside the same group when the
  // active layer is in one.
  commit('New Layer', { ...doc, layers: insertNextTo(doc.layers, layer, activeLayerId, 1) });
  setActiveLayer(layer.id);
  notify('Painting on a new layer');
  return targetOf(layer);
}

/** Write the baked pixels back, producing the document to commit. */
export function applyStrokeToLayer(
  doc: EditorDocument,
  target: PaintTarget,
  pixels: string,
): EditorDocument {
  if (target.field === 'mask') return patchMask(doc, target.layerId, { src: pixels });
  return updateLayer(doc, target.layerId, { [target.field]: pixels } as Partial<Layer>);
}
